import type { Locales } from '../composables/i18n';
import { useCommonStore } from '~/stores/common';
import { useAuthToken } from '~/utils/token';
import { i18nGlobal } from '~/utils/i18n';
import { router as globalRouter, routeMap } from '~/utils/router';
import { commonRequests } from '~/requests/common/common.request';
import { resHandler } from '~/utils/http/handler';
import { globalMessage } from '~/utils/discreteApi';
import type { CreateAndUpdateDateProps } from '~/requests/common/types';

export type Dept = {
    deptId: number;
    parentId: number;
    deptLevels: string;
    deptName: string;
    orderNum: number;
    leader: string;
    phone: string;
    email: string;
    // 1正常0停用
    status: string;
    parentName: string;
    children: Dept[];
    // dict 1008
    deptType: string;
} & CreateAndUpdateDateProps;

export type UserLangListItem = {
    langCode: Locales;
    langName: string;
};

export type Role = {
    roleId: number;
    roleName: string;
    roleKey: string;
    roleSort: number;
    // 数据范围（1：所有数据权限；2：自定义数据权限；3：本部门数据权限；4：本部门及以下数据权限；5：仅本人数据权限）
    dataScope: string;
    // 菜单树选择项是否关联显示（ 0：父子不互相关联显示 1：父子互相关联显示）
    menuCheckStrictly: boolean;
    // 部门树选择项是否关联显示（0：父子不互相关联显示 1：父子互相关联显示 ）
    deptCheckStrictly: boolean;
    // 1正常0停用
    status: number;
    // 0代表存在 1代表删除
    delFlag: number;
    flag: boolean;
    menuIds: number[];
    deptIds: number[];
    permissions: string[];
    remark: string;
    // 区分下系统1->POMS,2->SNW
    sysId: number;
} & CreateAndUpdateDateProps;

export type UserInfo = {
    deptType: string;
    userid: number;
    userId: number;
    deptId: number;
    username: string;
    realName: string;
    email: string;
    phoneNumber: string;
    sex: string;
    avatar: string;
    dept: Dept;
    roles: Role[];
    roleIds: number[];
    postIds: number[];
    roleId: number;
    customerId: number;
    remark: string;
    childrenDeptIdList: number[];
    areaCode: number;
    // 区分下系统1->POMS,2->SNW
    sysId: number;
    idaasUser: number;
    pwdRemindDay: string;
    deleteCaseFlag: number;
    brand: string;
    loginFlag: string;
    systemList: number[];
} & CreateAndUpdateDateProps;

export type UploadProgressListItem = { name: string; percentage: number; id: number };

const useUploadProgress = () => {
    const uploadItemIndex = ref(0);
    const showUploadProgress = ref(false);
    const uploadList = ref<UploadProgressListItem[]>([]);

    return {
        uploadItemIndex,
        uploadList,
        showUploadProgress,
    };
};

export const useUserStore = defineStore('user', () => {
    const commonStore = useCommonStore();
    const instanceRouter = useRouter();
    const { brand } = useAuthToken();
    const { availableLocales } = useAvailableLocaleList();
    const isTopMen = useStorage('my-isTopMen', false, localStorage);
    const userInfo = ref<UserInfo | null>(null);
    const permissions = ref<any>(null);
    const isLogout = ref(false);

    const uploadProgress = useUploadProgress();
    const switchMenu = (e) => {
        isTopMen.value = e;
    };
    const logoutCb = async () => {
        const router = instanceRouter ?? globalRouter;
        routeMap.clear();
        commonStore.formattedRoutes = [];
        const sysid = userInfo.value?.sysId;
        await commonStore.clearAuth();
        userInfo.value = null;
        await commonStore.clearAuth();
        if (!['development', 'test', 'theme'].includes(import.meta.env.MODE)) {
            if (isLogout.value) {
                window.history.replaceState({}, '');
                window.location.replace(import.meta.env.VITE_IDP_LOGOUT_URL);
            } else
                router.push({
                    path: '/auth/oauth',
                    query: {
                        from: router.currentRoute.value.path,
                    },
                });
            return globalMessage.success(i18nGlobal.t('auth.logout_success'));
        }
        // if (sysid !== 2) {
        isLogout.value = false;
        await router.push('/auth/login');
        // }
        return globalMessage.success(i18nGlobal.t('auth.logout_success'));
    };

    const logout = async () => {
        if (!commonStore.authToken) {
            await logoutCb();
            return;
        }
        const res = await commonRequests.logout();
        const isSuccess = await resHandler(res);
        if (isSuccess) {
            isLogout.value = true;
            isTopMen.value = true;
            brand.value = null;
            await logoutCb();
        }
    };

    const getUserInfo = async () => {
        const router = instanceRouter ?? globalRouter;
        if (!userInfo.value) {
            // commonRequests.cdcAuth();
            return commonRequests.getUserInfo().then(async (res) => {
                await resHandler(res, {
                    onSuccess: ({ res }) => {
                        userInfo.value = res.user;
                        permissions.value = res.permissions;
                        userInfo.value.pwdRemindDay = res.pwdRemindDay;
                        userInfo.value.loginFlag = res.loginFlag;
                        userInfo.value.systemList = res.systemList;
                        i18nGlobal.locale.value = res.userLang;
                        commonStore.locale = res.userLang;
                        availableLocales.value = res.userLangList;
                        // if (userInfo.value.idaasUser === 1) commonRequests.cdcAuth();
                    },
                });
            });
        }
    };

    return {
        isTopMen,
        logout,
        logoutCb,
        permissions,
        userInfo,
        isLogout,
        getUserInfo,
        switchMenu,
        ...uploadProgress,
    };
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
